import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, Lock, Globe, Award, ChevronRight } from 'lucide-react';
import { API, authFetch } from '@/App';
import GlowBadge from '@/components/GlowBadge';

export default function SearchPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const debounceRef = useRef(null);

  useEffect(() => {
    const saved = localStorage.getItem('gloverse_recent_searches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await authFetch(`${API}/users/search?q=${encodeURIComponent(query)}`, {
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Search results:', data);
        setSearchResults(data);
      } else {
        console.error('Search failed:', response.status);
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const onSearchInput = (value) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => handleSearch(value), 300);
  };

  const handleUserClick = (user) => {
    // Save to recent searches
    const updated = [user, ...recentSearches.filter(u => u.user_id !== user.user_id)].slice(0, 10);
    setRecentSearches(updated);
    localStorage.setItem('gloverse_recent_searches', JSON.stringify(updated));
    
    navigate(`/profile/${user.user_id}`);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('gloverse_recent_searches');
  };

  return (
    <div className="min-h-screen pb-24">
      {/* Search Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              data-testid="search-input"
              type="text"
              value={searchQuery}
              onChange={(e) => {
                onSearchInput(e.target.value);
              }}
              placeholder="Search by name, username, or email..."
              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-border bg-white/50 focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all outline-none"
              autoFocus
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Glow Creators Banner */}
        {!searchQuery && (
          <button
            data-testid="glow-creators-banner"
            onClick={() => navigate('/glow-creators')}
            className="w-full flex items-center gap-3 p-4 mb-5 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/60 hover:border-amber-300 transition-colors text-left"
          >
            <div className="flex -space-x-1">
              <GlowBadge tier="glow_pro" size="md" />
              <GlowBadge tier="glow" size="md" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-sm text-foreground">Glow Creators</p>
              <p className="text-xs text-muted-foreground">Discover verified beauty creators</p>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : searchQuery ? (
          /* Search Results */
          <div className="space-y-2">
            <h2 className="text-sm font-medium text-muted-foreground mb-4">
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
            </h2>
            {searchResults.length === 0 ? (
              <div className="text-center py-12">
                <User className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No users found for "{searchQuery}"</p>
              </div>
            ) : (
              searchResults.map(user => (
                <button
                  key={user.user_id}
                  data-testid={`user-result-${user.user_id}`}
                  onClick={() => handleUserClick(user)}
                  className="w-full flex items-center gap-4 p-4 bg-white rounded-xl border border-border hover:border-primary/50 transition-colors text-left"
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold">
                      {user.name?.charAt(0) || 'U'}
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-bold">{user.name}</p>
                      <GlowBadge tier={user.glow_badge} />
                      {user.is_private ? (
                        <Lock className="w-3 h-3 text-muted-foreground" />
                      ) : (
                        <Globe className="w-3 h-3 text-muted-foreground" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">@{user.username || user.name?.replace(/\s+/g, '').toLowerCase()}</p>
                  </div>
                  <div className="text-right text-xs text-muted-foreground">
                    <p>{user.followers_count || 0} followers</p>
                  </div>
                </button>
              ))
            )}
          </div>
        ) : (
          /* Recent Searches */
          <div>
            {recentSearches.length > 0 && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-medium text-muted-foreground">Recent Searches</h2>
                  <button
                    onClick={clearRecentSearches}
                    className="text-sm text-primary hover:underline"
                  >
                    Clear all
                  </button>
                </div>
                <div className="space-y-2">
                  {recentSearches.map(user => (
                    <button
                      key={user.user_id}
                      onClick={() => handleUserClick(user)}
                      className="w-full flex items-center gap-4 p-3 bg-white/50 rounded-xl hover:bg-white transition-colors text-left"
                    >
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold text-sm">
                          {user.name?.charAt(0) || 'U'}
                        </div>
                      )}
                      <div>
                        <p className="font-medium">{user.name}</p>
                        <p className="text-sm text-muted-foreground">@{user.username || user.name?.replace(/\s+/g, '').toLowerCase()}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
            
            {recentSearches.length === 0 && (
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">Search for users to follow</p>
                <p className="text-sm text-muted-foreground mt-1">Find beauty enthusiasts and creators</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
